import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Handshake, Target, Ban, Zap, Lock, Diamond, Shield, ArrowRight } from 'lucide-react';

export default function WhyFree() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Why LinkDiry Is Free  -  No Catch, No Limits"
        description="LinkDiry is a free backlink price checker with no limits. We earn referral fees from marketplaces when you click through. You pay the same price. Compare backlink prices for free, forever."
        canonical="/why-free"
      />
      <TopNav />

      <div className="max-w-5xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-sm font-semibold mb-4">100% FREE</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6">
            Yes, it's completely free.<br />Here's why.
          </h1>
        </div>

        {/* Price Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-baseline gap-4 mb-3">
            <span className="text-7xl sm:text-8xl font-extrabold bg-gradient-to-r from-brand-600 to-brand-400 bg-clip-text text-transparent">$0</span>
            <div className="text-left">
              <p className="text-2xl font-bold text-slate-900">Forever. No catch.</p>
              <p className="text-slate-500">No credit card · No limits · No premium paywall</p>
            </div>
          </div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mt-6">
            We believe backlink price comparison should not cost you money. Other tools charge you just to see where you can buy cheaper  -  that is backwards. Our free backlink price checker lets you compare backlink prices across 50+ sellers without paying a cent.
          </p>
        </div>

        {/* Explanation Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: Handshake,
              title: 'Marketplaces Pay Us, Not You',
              desc: 'We earn small referral fees when you click through to a seller. You pay the exact same price whether you find them through us or not.',
            },
            {
              icon: Target,
              title: 'We Win When You Save Money',
              desc: 'We only make money when you find a good deal and click through. If you do not save money, we do not get paid. Simple as that.',
            },
            {
              icon: Ban,
              title: 'No Ads, No Upsells, No Data Selling',
              desc: 'No banner ads, no data selling, no paywall gates. Your data stays yours. We make money from seller referrals only.',
            },
            {
              icon: Zap,
              title: 'Unlimited Everything',
              desc: 'Unlimited searches, bulk checks, exports, alerts. No daily caps, no credit limits, no "upgrade to unlock" tricks.',
            },
            {
              icon: Lock,
              title: 'No Account Required',
              desc: 'Search without signing up. No email, no password required. Create an account only if you want alerts and watchlists.',
            },
            {
              icon: Diamond,
              title: 'Transparency Is Our Product',
              desc: 'Every seller, every price, unbiased quality scores. The cheapest option always shows first. No sponsored placements.',
            },
          ].map(item => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="p-6 border border-slate-200 rounded-2xl bg-white shadow-card hover:shadow-card-hover hover:border-brand-300 transition-all">
                <div className="w-11 h-11 bg-brand-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-brand-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Promise Card */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center mb-16">
          <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-brand-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-3">We Will Never Charge for Price Comparisons</h2>
          <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
            We will never charge users for price comparisons. Ever. The SEO industry is full of overpriced tools with hidden fees.
            We're here to change that. Free price transparency makes the entire market more efficient  -  and that benefits everyone.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-brand-500/20 gap-2">
            Start Searching  -  It's Free <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-slate-400 mt-3">No signup required. Just search.</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
